export type DocType = "invoice" | "offer" | "cancellation" | "credit_note";
export type Series = "RE" | "AN" | "GU";
export type DocStatus = "draft" | "open" | "partially_paid" | "paid" | "cancelled" | "sent" | "accepted" | "rejected" | "partially_invoiced" | "invoiced";
export type Origin = "manual" | "open_items" | "offer" | "cancellation" | "series" | "import";

export interface Recipient { name: string; street: string; zip: string; city: string; country: string; vatId: string | null; customerNumber: string | null }

export type PositionSource =
  | { kind: "time"; projectId: number; hours: number; rateCents: number; timeEntryIds: number[] }
  | { kind: "flat"; projectId: number; timeEntryIds: number[] }
  | { kind: "expense"; expenseShareId: number; payee: string }
  | { kind: "offer"; offerPositionId: number; share: number }
  | { kind: "deduction"; documentId: number; number: string; date: string };

export interface Position { id: number; sort: number; title: string; description: string; quantity: number; unit: string; unitPriceCents: number; totalCents: number; source: PositionSource | null }

export interface Document {
  id: number; type: DocType; series: Series; number: string | null; status: DocStatus; statusLabel: string;
  clientId: number; clientName: string; origin: Origin; offerId: number | null; cancelsId: number | null; cancelledById: number | null;
  partial: { kind: "full" | "percent" | "amount" | "positions" | "final"; value: number | null } | null;
  date: string | null; periodFrom: string | null; periodTo: string | null; validUntil: string | null; timeToPay: number | null; dueDate: string | null;
  title: string; intro: string; closing: string; taxNote: string; recipient: Recipient; contactPerson: string;
  totalCents: number; paidCents: number; positions: Position[];
  finalizedAt: string | null; sentAt: string | null; sentTo: string | null; remindedAt: string | null; paidAt: string | null; cancelledAt: string | null;
  acceptedAt: string | null; rejectedAt: string | null;
  createdAt: string; updatedAt: string;
}

export interface DocumentRow { id: number; type: DocType; number: string | null; status: DocStatus; statusLabel: string; clientId: number; clientName: string; date: string | null; periodLabel: string; totalCents: number; paidCents: number; dueDate: string | null }

/** `unitPrice` is EUR (agents and the editor speak euros); it is stored as cents. */
export interface PositionInput { id?: number; delete?: boolean; title?: string; description?: string; quantity?: number; unit?: string; unitPrice?: number }

export interface DocumentInput { date?: string; periodFrom?: string | null; periodTo?: string | null; validUntil?: string | null; timeToPay?: number; title?: string; intro?: string; closing?: string; recipient?: Partial<Recipient>; positions?: PositionInput[]; order?: number[] }

export const DOC_TYPE_LABELS: Record<DocType, string> = { invoice: "Rechnung", offer: "Angebot", cancellation: "Stornorechnung", credit_note: "Gutschrift" };

/** „Rechnung RE-1086“, „Angebot AN-1006“; a draft is „Rechnung (Entwurf)“. */
export function documentLabel(doc: Pick<Document, "type" | "number">): string {
  return `${DOC_TYPE_LABELS[doc.type]} ${doc.number ?? "(Entwurf)"}`;
}

export const STATUS_LABELS: Record<DocStatus, string> = {
  draft: "Entwurf",
  open: "offen",
  partially_paid: "teilweise bezahlt",
  paid: "bezahlt",
  cancelled: "storniert",
  sent: "versendet",
  accepted: "angenommen",
  rejected: "abgelehnt",
  partially_invoiced: "teilweise verrechnet",
  invoiced: "verrechnet",
};
